import React, { useState, useEffect } from 'react';
import { Shield, Package, Globe, TrendingUp, CheckCircle, Users, Truck, Factory, Store, User, ArrowRight, Menu, X, FileText, Lock, Database, BarChart3, Leaf, Award } from 'lucide-react';

function App() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const useCases = [
    {
      title: "Indonesian Tuna",
      icon: "🐟",
      description: "Blockchain traceability for tuna supply chain, monitoring performance and reducing operational gaps.",
      benefits: ["17% of world's tuna supply", "Reduced health and safety risks", "Fewer product spoilage"]
    },
    {
      title: "Thai Farmed Shrimp",
      icon: "🦐",
      description: "Digital chain of custody for shrimp production, enhancing brand trust and regulatory compliance.",
      benefits: ["Top 5 global producer", "Health & safety risk reduction", "Export compliance"]
    },
    {
      title: "Brazilian Soy",
      icon: "🌾",
      description: "Traceability for soy supply chain with improved data sharing and visibility for compliance.",
      benefits: ["World's largest producer", "Deforestation prevention", "Streamlined compliance"]
    },
    {
      title: "Brazilian Beef",
      icon: "🥩",
      description: "Greater transparency and auditability for beef supply chain, managing health and safety risks.",
      benefits: ["1.44M metric tons exported", "Consumer confidence", "Operational efficiency"]
    }
  ];

  const problems = [
    "Increased global focus on product safety",
    "Quality assurance challenges",
    "Counterfeit products",
    "Cargo rejections due to lack of proof",
    "Low brand demand and customer loyalty",
    "High operational costs",
    "Slow response times in supply chain"
  ];

  const solutions = [
    "Documents submitted digitally to next workflow level",
    "Push back mechanism if errors found",
    "Work metrics at each stage measured",
    "AI-based auto guide on forms filling",
    "More transparency at every export stage",
    "Dashboard for ministry performance monitoring",
    "High reliability and security",
    "Access for multiple departments and countries"
  ];

  const benefits = [
    {
      icon: <Users className="w-8 h-8" />,
      title: "Policy Makers",
      points: [
        "Safe & Security with standardized procedure",
        "Transparent and auditable",
        "All transactions visible to authorized participants",
        "100% reliability of data",
        "No single point of failure",
        "Immutable and irrevocable transactions"
      ]
    },
    {
      icon: <Truck className="w-8 h-8" />,
      title: "Exporters",
      points: [
        "Cost effective and efficient documentation",
        "Hassle-free documentation process",
        "Standardized and low cost",
        "Direct peer-to-peer transactions",
        "Automatic ledger updates",
        "No reconciliation work required"
      ]
    },
    {
      icon: <Globe className="w-8 h-8" />,
      title: "Government",
      points: [
        "Error-free automation system",
        "Smart advance custom clearance",
        "Global accepted blockchain solution",
        "Traceability increases India's global picture",
        "100% transparent international trade",
        "Building global trusted brand"
      ]
    }
  ];

  const features = [
    {
      icon: <Package className="w-12 h-12" />,
      title: "Product Traceability",
      description: "Track products from source to customer with complete transparency and immutable records."
    },
    {
      icon: <Shield className="w-12 h-12" />,
      title: "Quality Assurance",
      description: "Ensure product authenticity and quality at every stage of the supply chain."
    },
    {
      icon: <BarChart3 className="w-12 h-12" />,
      title: "Real-time Analytics",
      description: "Monitor supply chain performance with comprehensive dashboards and metrics."
    },
    {
      icon: <Lock className="w-12 h-12" />,
      title: "Secure & Compliant",
      description: "Patent-protected blockchain solution ensuring regulatory compliance and data security."
    }
  ];

  return (
    <div style={{minHeight: '100vh', background: 'linear-gradient(to bottom right, #0f766e, #0d9488, #134e4a)', color: 'white', fontFamily: 'system-ui, -apple-system, sans-serif'}}>
      
      {/* Navigation */}
      <nav style={{
        position: 'fixed',
        width: '100%',
        zIndex: 50,
        transition: 'all 0.3s',
        backgroundColor: scrolled ? 'rgba(15, 118, 110, 0.95)' : 'transparent',
        backdropFilter: scrolled ? 'blur(12px)' : 'none',
        boxShadow: scrolled ? '0 10px 15px -3px rgba(0, 0, 0, 0.1)' : 'none'
      }}>
        <div style={{maxWidth: '1280px', margin: '0 auto', padding: '0 1rem'}}>
          <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', height: '80px'}}>
            
            {/* Logo */}
            <div style={{display: 'flex', alignItems: 'center', gap: '12px'}}>
              <div style={{
                width: '48px',
                height: '48px',
                background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                borderRadius: '12px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)'
              }}>
                <Package style={{width: '28px', height: '28px', color: 'white'}} />
              </div>
              <span style={{fontSize: '1.5rem', fontWeight: 'bold', letterSpacing: '-0.025em'}}>
                Track<span style={{color: '#5eead4'}}>My</span>Chain
              </span>
            </div>

            {/* Desktop Menu */}
            <div style={{display: window.innerWidth >= 768 ? 'flex' : 'none', alignItems: 'center', gap: '2rem'}}>
              <a href="#features" style={{color: 'white', textDecoration: 'none'}}>Features</a>
              <a href="#solutions" style={{color: 'white', textDecoration: 'none'}}>Solutions</a>
              <a href="#usecases" style={{color: 'white', textDecoration: 'none'}}>Use Cases</a>
              <a href="#benefits" style={{color: 'white', textDecoration: 'none'}}>Benefits</a>
              <button style={{
                background: 'linear-gradient(to right, #14b8a6, #0d9488)',
                padding: '0.5rem 1.5rem',
                borderRadius: '8px',
                fontWeight: '600',
                border: 'none',
                color: 'white',
                cursor: 'pointer',
                boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1)'
              }}>
                Get Started
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button 
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              style={{
                display: window.innerWidth < 768 ? 'block' : 'none',
                padding: '0.5rem',
                background: 'none',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}
            >
              {mobileMenuOpen ? <X /> : <Menu />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div style={{backgroundColor: '#0f766e', borderTop: '1px solid rgba(255, 255, 255, 0.1)'}}>
            <div style={{padding: '1rem', display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
              <a href="#features" style={{display: 'block', padding: '0.5rem 0', color: 'white', textDecoration: 'none'}}>Features</a>
              <a href="#solutions" style={{display: 'block', padding: '0.5rem 0', color: 'white', textDecoration: 'none'}}>Solutions</a>
              <a href="#usecases" style={{display: 'block', padding: '0.5rem 0', color: 'white', textDecoration: 'none'}}>Use Cases</a>
              <a href="#benefits" style={{display: 'block', padding: '0.5rem 0', color: 'white', textDecoration: 'none'}}>Benefits</a>
              <button style={{
                width: '100%',
                background: 'linear-gradient(to right, #14b8a6, #0d9488)',
                padding: '0.5rem 1.5rem',
                borderRadius: '8px',
                fontWeight: '600',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}>
                Get Started
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <div style={{position: 'relative', paddingTop: '8rem', paddingBottom: '5rem', padding: '8rem 1rem 5rem', overflow: 'hidden'}}>
        <div style={{position: 'absolute', inset: 0, opacity: 0.15, pointerEvents: 'none'}}>
          <div style={{
            position: 'absolute',
            top: '80px',
            left: '40px',
            width: '288px',
            height: '288px',
            backgroundColor: '#14b8a6',
            borderRadius: '50%',
            filter: 'blur(96px)',
            animation: 'pulse 3s ease-in-out infinite'
          }}></div>
          <div style={{
            position: 'absolute',
            bottom: '80px',
            right: '40px',
            width: '384px',
            height: '384px',
            backgroundColor: '#0d9488',
            borderRadius: '50%',
            filter: 'blur(96px)',
            animation: 'pulse 3s ease-in-out infinite',
            animationDelay: '0.7s'
          }}></div>
        </div>

        <div style={{maxWidth: '1280px', margin: '0 auto', position: 'relative', zIndex: 10}}>
          <div style={{textAlign: 'center', maxWidth: '896px', margin: '0 auto'}}>
            <div style={{display: 'inline-block', marginBottom: '1.5rem'}}>
              <span style={{
                background: 'rgba(20, 184, 166, 0.3)',
                color: '#ccfbf1',
                padding: '0.5rem 1rem',
                borderRadius: '9999px',
                fontSize: '0.875rem',
                fontWeight: '600',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(94, 234, 212, 0.3)'
              }}>
                Blockchain-Powered Supply Chain Traceability
              </span>
            </div>
            
            <h1 style={{fontSize: 'clamp(2.5rem, 5vw, 4.5rem)', fontWeight: 'bold', marginBottom: '1.5rem', lineHeight: 1.2}}>
              Advanced Custom Clearances &
              <span style={{
                display: 'block',
                background: 'linear-gradient(to right, #5eead4, #2dd4bf, #99f6e4)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text'
              }}>
                Supply Chain Logistics
              </span>
            </h1>
            
            <p style={{fontSize: 'clamp(1.125rem, 2vw, 1.5rem)', color: '#ccfbf1', marginBottom: '2.5rem', lineHeight: 1.75}}>
              Patent-protected blockchain solution for complete supply chain transparency, from source to customer
            </p>

            <div style={{display: 'flex', flexDirection: 'column', gap: '1rem', justifyContent: 'center', alignItems: 'center'}}>
              <button style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: 'linear-gradient(to right, #14b8a6, #0d9488)',
                padding: '1rem 2rem',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '1.125rem',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
                border: 'none',
                color: 'white',
                cursor: 'pointer'
              }}>
                Explore Solutions
                <ArrowRight style={{width: '1.25rem', height: '1.25rem'}} />
              </button>
              <button style={{
                border: '2px solid #5eead4',
                background: 'transparent',
                padding: '1rem 2rem',
                borderRadius: '8px',
                fontWeight: 'bold',
                fontSize: '1.125rem',
                color: 'white',
                cursor: 'pointer',
                backdropFilter: 'blur(4px)'
              }}>
                View Use Cases
              </button>
            </div>
          </div>

          {/* Platform Users */}
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
            marginTop: '5rem',
            maxWidth: '1000px',
            margin: '5rem auto 0'
          }}>
            {[
              {icon: <User />, label: 'Farmers', color: '#60a5fa'},
              {icon: <Factory />, label: 'Manufacturers', color: '#f97316'},
              {icon: <Truck />, label: 'Distributors', color: '#ec4899'},
              {icon: <Store />, label: 'Retailers', color: '#a855f7'},
              {icon: <Users />, label: 'Customers', color: '#14b8a6'}
            ].map((user, idx) => (
              <div key={idx} style={{
                background: 'rgba(20, 184, 166, 0.2)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(94, 234, 212, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem',
                textAlign: 'center',
                transition: 'all 0.3s'
              }}>
                <div style={{color: user.color, marginBottom: '0.75rem', display: 'flex', justifyContent: 'center'}}>
                  {React.cloneElement(user.icon, {style: {width: '32px', height: '32px'}})}
                </div>
                <div style={{fontWeight: '600', fontSize: '1.125rem'}}>{user.label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Problems Section */}
      <div style={{padding: '5rem 1rem', background: 'rgba(0, 0, 0, 0.2)'}}>
        <div style={{maxWidth: '1280px', margin: '0 auto'}}>
          <div style={{textAlign: 'center', marginBottom: '4rem'}}>
            <h2 style={{fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'bold', marginBottom: '1rem'}}>
              Summary of Problems
            </h2>
            <p style={{fontSize: '1.25rem', color: '#ccfbf1'}}>Challenges in Track & Trace</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            {problems.map((problem, idx) => (
              <div key={idx} style={{
                background: 'rgba(20, 184, 166, 0.15)',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(94, 234, 212, 0.2)',
                borderRadius: '12px',
                padding: '1.5rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem'
              }}>
                <div style={{
                  width: '24px',
                  height: '24px',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #ef4444, #dc2626)',
                  flexShrink: 0,
                  marginTop: '2px'
                }}></div>
                <p style={{lineHeight: 1.6, color: '#e0f2fe'}}>{problem}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Solutions Section */}
      <div id="solutions" style={{padding: '5rem 1rem'}}>
        <div style={{maxWidth: '1280px', margin: '0 auto'}}>
          <div style={{textAlign: 'center', marginBottom: '4rem'}}>
            <h2 style={{fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'bold', marginBottom: '1rem'}}>
              Our Suggested Solution
            </h2>
            <p style={{fontSize: '1.25rem', color: '#ccfbf1'}}>Blockchain Technology Smart Contract Based Solution</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem'
          }}>
            {solutions.map((solution, idx) => (
              <div key={idx} style={{
                background: 'linear-gradient(to bottom right, rgba(20, 184, 166, 0.25), rgba(13, 148, 136, 0.15))',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(94, 234, 212, 0.3)',
                borderRadius: '12px',
                padding: '1.5rem',
                display: 'flex',
                alignItems: 'flex-start',
                gap: '1rem'
              }}>
                <CheckCircle style={{width: '24px', height: '24px', color: '#22d3ee', flexShrink: 0, marginTop: '2px'}} />
                <p style={{lineHeight: 1.6}}>{solution}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" style={{padding: '5rem 1rem', background: 'rgba(0, 0, 0, 0.2)'}}>
        <div style={{maxWidth: '1280px', margin: '0 auto'}}>
          <div style={{textAlign: 'center', marginBottom: '4rem'}}>
            <h2 style={{fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'bold', marginBottom: '1rem'}}>
              Platform Functionality
            </h2>
            <p style={{fontSize: '1.25rem', color: '#ccfbf1'}}>Advanced blockchain-powered features</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            gap: '2rem'
          }}>
            {features.map((feature, idx) => (
              <div key={idx} style={{
                background: 'linear-gradient(to bottom right, rgba(20, 184, 166, 0.3), rgba(13, 148, 136, 0.2))',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(94, 234, 212, 0.3)',
                borderRadius: '16px',
                padding: '2rem',
                transition: 'all 0.3s',
                cursor: 'pointer'
              }}>
                <div style={{color: '#5eead4', marginBottom: '1rem'}}>
                  {feature.icon}
                </div>
                <h3 style={{fontSize: '1.5rem', fontWeight: 'bold', marginBottom: '0.75rem'}}>{feature.title}</h3>
                <p style={{color: '#ccfbf1', lineHeight: 1.75}}>{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Use Cases Section */}
      <div id="usecases" style={{padding: '5rem 1rem'}}>
        <div style={{maxWidth: '1280px', margin: '0 auto'}}>
          <div style={{textAlign: 'center', marginBottom: '4rem'}}>
            <h2 style={{fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'bold', marginBottom: '1rem'}}>
              Global Successful Use Cases
            </h2>
            <p style={{fontSize: '1.25rem', color: '#ccfbf1'}}>Supply chain logistics traceability on Blockchain Technology</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '2rem'
          }}>
            {useCases.map((useCase, idx) => (
              <div key={idx} style={{
                background: 'linear-gradient(to bottom right, rgba(20, 184, 166, 0.25), rgba(19, 78, 74, 0.3))',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(94, 234, 212, 0.3)',
                borderRadius: '16px',
                padding: '2rem',
                transition: 'all 0.3s'
              }}>
                <div style={{fontSize: '3rem', marginBottom: '1rem'}}>{useCase.icon}</div>
                <h3 style={{fontSize: '1.75rem', fontWeight: 'bold', marginBottom: '1rem'}}>{useCase.title}</h3>
                <p style={{color: '#ccfbf1', marginBottom: '1.5rem', lineHeight: 1.75}}>{useCase.description}</p>
                <div style={{display: 'flex', flexDirection: 'column', gap: '0.5rem'}}>
                  {useCase.benefits.map((benefit, bidx) => (
                    <div key={bidx} style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
                      <CheckCircle style={{width: '16px', height: '16px', color: '#22d3ee', flexShrink: 0}} />
                      <span style={{fontSize: '0.875rem', color: '#e0f2fe'}}>{benefit}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Benefits Section */}
      <div id="benefits" style={{padding: '5rem 1rem', background: 'rgba(0, 0, 0, 0.2)'}}>
        <div style={{maxWidth: '1280px', margin: '0 auto'}}>
          <div style={{textAlign: 'center', marginBottom: '4rem'}}>
            <h2 style={{fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'bold', marginBottom: '1rem'}}>
              Our Solution Benefits
            </h2>
            <p style={{fontSize: '1.25rem', color: '#ccfbf1'}}>Value for all stakeholders</p>
          </div>

          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '2rem'
          }}>
            {benefits.map((benefit, idx) => (
              <div key={idx} style={{
                background: 'linear-gradient(to bottom right, rgba(20, 184, 166, 0.2), rgba(13, 148, 136, 0.15))',
                backdropFilter: 'blur(4px)',
                border: '1px solid rgba(94, 234, 212, 0.3)',
                borderRadius: '16px',
                padding: '2rem'
              }}>
                <div style={{display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1.5rem'}}>
                  <div style={{color: '#5eead4'}}>
                    {benefit.icon}
                  </div>
                  <h3 style={{fontSize: '1.5rem', fontWeight: 'bold'}}>{benefit.title}</h3>
                </div>
                <div style={{display: 'flex', flexDirection: 'column', gap: '0.75rem'}}>
                  {benefit.points.map((point, pidx) => (
                    <div key={pidx} style={{display: 'flex', alignItems: 'flex-start', gap: '0.75rem'}}>
                      <CheckCircle style={{width: '20px', height: '20px', color: '#22d3ee', flexShrink: 0, marginTop: '2px'}} />
                      <span style={{fontSize: '0.95rem', lineHeight: 1.6, color: '#e0f2fe'}}>{point}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div style={{padding: '5rem 1rem'}}>
        <div style={{
          maxWidth: '896px',
          margin: '0 auto',
          textAlign: 'center',
          background: 'linear-gradient(to right, #0d9488, #0f766e)',
          borderRadius: '24px',
          padding: '3rem',
          boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          border: '1px solid rgba(94, 234, 212, 0.3)'
        }}>
          <h2 style={{fontSize: 'clamp(2rem, 4vw, 3rem)', fontWeight: 'bold', marginBottom: '1.5rem'}}>
            Ready to Transform Your Supply Chain?
          </h2>
          <p style={{fontSize: '1.25rem', color: '#ccfbf1', marginBottom: '2rem'}}>
            Join the blockchain revolution in supply chain traceability and logistics
          </p>
          <button style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            background: 'white',
            color: '#0f766e',
            padding: '1rem 2.5rem',
            borderRadius: '8px',
            fontWeight: 'bold',
            fontSize: '1.125rem',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
            border: 'none',
            cursor: 'pointer'
          }}>
            Contact Us
            <ArrowRight style={{width: '1.25rem', height: '1.25rem'}} />
          </button>
        </div>
      </div>

      {/* Footer */}
      <footer style={{background: '#042f2e', padding: '3rem 1rem', borderTop: '1px solid rgba(94, 234, 212, 0.2)'}}>
        <div style={{maxWidth: '1280px', margin: '0 auto'}}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '2rem',
            marginBottom: '2rem'
          }}>
            <div>
              <div style={{display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '1rem'}}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  background: 'linear-gradient(135deg, #14b8a6, #0d9488)',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Package style={{width: '24px', height: '24px', color: 'white'}} />
                </div>
                <span style={{fontSize: '1.25rem', fontWeight: 'bold'}}>TrackMyChain</span>
              </div>
              <p style={{color: '#5eead4', fontSize: '0.875rem'}}>Blockchain-powered supply chain traceability solutions</p>
            </div>
            
            <div>
              <h4 style={{fontWeight: 'bold', marginBottom: '1rem'}}>Solutions</h4>
              <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem', color: '#99f6e4', fontSize: '0.875rem'}}>
                <li><a href="#" style={{color: '#99f6e4', textDecoration: 'none'}}>Product Traceability</a></li>
                <li><a href="#" style={{color: '#99f6e4', textDecoration: 'none'}}>Custom Clearance</a></li>
                <li><a href="#" style={{color: '#99f6e4', textDecoration: 'none'}}>Logistics Management</a></li>
              </ul>
            </div>
            
            <div>
              <h4 style={{fontWeight: 'bold', marginBottom: '1rem'}}>Company</h4>
              <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem', color: '#99f6e4', fontSize: '0.875rem'}}>
                <li><a href="#" style={{color: '#99f6e4', textDecoration: 'none'}}>About Us</a></li>
                <li><a href="#" style={{color: '#99f6e4', textDecoration: 'none'}}>Careers</a></li>
                <li><a href="#" style={{color: '#99f6e4', textDecoration: 'none'}}>Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h4 style={{fontWeight: 'bold', marginBottom: '1rem'}}>Legal</h4>
              <ul style={{listStyle: 'none', padding: 0, display: 'flex', flexDirection: 'column', gap: '0.5rem', color: '#99f6e4', fontSize: '0.875rem'}}>
                <li><a href="#" style={{color: '#99f6e4', textDecoration: 'none'}}>Privacy Policy</a></li>
                <li><a href="#" style={{color: '#99f6e4', textDecoration: 'none'}}>Terms of Service</a></li>
                <li><a href="#" style={{color: '#99f6e4', textDecoration: 'none'}}>Security</a></li>
              </ul>
            </div>
          </div>
          
          <div style={{borderTop: '1px solid rgba(94, 234, 212, 0.2)', paddingTop: '2rem', textAlign: 'center', color: '#99f6e4', fontSize: '0.875rem'}}>
            <p>&copy; 2025 TrackMyChain. All rights reserved. Blockchain-powered supply chain solutions.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default App
